import '@apps/web/features/learn_phoebe/ui/learn_phoebe.css';

import {
    Check,
    ChevronRight,
    FileSpreadsheet,
    Play,
    UserRound,
} from 'lucide-react';
import { Fragment, useMemo } from 'react';

import {
    ENTITY_CONFIG,
    ENTITY_PILL_CLASSES,
    USER_MESSAGE_BUBBLE_CLASSES,
} from '@apps/web/components/phoebe_chat/entity_visuals';
import {
    type ChatTurn,
    type LearnTask,
    type MentionPillData,
    type MentionPillKind,
} from '@apps/web/features/learn_phoebe/model/learn_content';
import {
    buildComposerView,
    buildComposeSteps,
    type ComposerView,
} from '@apps/web/features/learn_phoebe/model/playback';
import {
    useComposePlayback,
    useRevealedTurnCount,
} from '@apps/web/features/learn_phoebe/model/use_chat_playback';
import { useTypewriterText } from '@apps/web/hooks/use_typewriter_text';
import { cn } from '@apps/web/lib/utils';

// A read-only chat that plays a task's scripted conversation: the composer
// types the ask (pausing on @ for the picker), sends, then the turns reveal
// with the real chat's typewriter. Remount (via key) to restart playback.
// Used by the Learn Phoebe page and by the sidebar checklist dialog.
export function FakeChat({
    task,
    className,
}: {
    readonly task: LearnTask | null;
    readonly className?: string;
}) {
    const steps = useMemo(
        () => (task === null ? [] : buildComposeSteps(task)),
        [task],
    );
    const consumedCount = useComposePlayback(steps);
    const composer = buildComposerView(steps, consumedCount);
    const turns = task?.turns ?? [];
    const revealedCount = useRevealedTurnCount(turns.length, composer.hasSent);
    const isPlaying = composer.hasSent && revealedCount < turns.length;

    return (
        <div
            className={cn(
                'bg-card flex flex-col rounded-xl border shadow-sm',
                // Sizes to the conversation: a floor so short scripts still
                // read as a chat, a cap so long ones scroll instead of
                // stretching the page.
                className ?? 'max-h-[32rem] min-h-64',
            )}
        >
            <div className="flex items-center justify-between border-b px-3 py-2">
                <div className="flex items-center gap-2">
                    <img
                        src="/icon.svg"
                        alt=""
                        aria-hidden="true"
                        className="size-4 shrink-0"
                    />
                    <span className="text-sm font-medium">New chat</span>
                </div>
                <span className="text-muted-foreground text-xs">Example</span>
            </div>
            {revealedCount === 0 ? (
                <div
                    aria-hidden="true"
                    className="flex flex-1 items-center justify-center gap-2 p-3 select-none"
                >
                    <img src="/icon.svg" alt="" className="size-5 shrink-0" />
                    <p className="text-sm font-medium">
                        How can I help you today?
                    </p>
                </div>
            ) : (
                <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-3">
                    {turns.slice(0, revealedCount).map((turn, index) => (
                        <div
                            key={index}
                            className="learn-phoebe-turn-in min-w-0"
                        >
                            <TurnContent turn={turn} />
                        </div>
                    ))}
                    {isPlaying ? (
                        <p
                            aria-hidden="true"
                            className="text-muted-foreground animate-pulse px-1 text-sm"
                        >
                            Thinking…
                        </p>
                    ) : null}
                </div>
            )}
            <ComposerBar
                view={composer}
                shiftGroupRow={task?.pickerShiftGroupRow ?? null}
            />
        </div>
    );
}

function PhoebeTurn({
    turn,
}: {
    readonly turn: Extract<ChatTurn, { readonly kind: 'phoebe' }>;
}) {
    // The shared hook snaps to full text under reduced motion, hidden tabs,
    // and in tests (no matchMedia). Turns remount per reveal, so animating
    // on mount matches the real chat's streaming feel.
    const revealedText = useTypewriterText(turn.text, {
        animate: true,
        animationKey: turn.text,
    });
    const isTextComplete = revealedText.length === turn.text.length;
    return (
        <div className="text-foreground text-sm leading-6">
            <p>{revealedText}</p>
            {turn.lines !== undefined && isTextComplete ? (
                <ul className="learn-phoebe-turn-in mt-1 space-y-0.5">
                    {turn.lines.map((line, index) => (
                        <li key={index}>{line}</li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
}

function MentionPill({
    kind,
    label,
}: {
    readonly kind: MentionPillKind;
    readonly label: string;
}) {
    const Icon = ENTITY_CONFIG[kind].icon;
    return (
        <span
            className={cn(
                'inline-flex items-center gap-0.5 rounded px-1 align-middle text-[13px] font-medium whitespace-nowrap',
                ENTITY_PILL_CLASSES[kind],
            )}
        >
            <Icon aria-hidden="true" className="size-3.5" />
            {label}
        </span>
    );
}

function TurnContent({ turn }: { readonly turn: ChatTurn }) {
    switch (turn.kind) {
        case 'user':
            return (
                <div className="flex justify-end">
                    <div
                        className={cn(
                            'w-fit max-w-full break-words',
                            USER_MESSAGE_BUBBLE_CLASSES,
                        )}
                    >
                        {turn.segments.map((segment, index) =>
                            'pill' in segment ? (
                                <MentionPill
                                    key={index}
                                    kind={segment.pill.kind}
                                    label={segment.pill.label}
                                />
                            ) : (
                                <Fragment key={index}>{segment.text}</Fragment>
                            ),
                        )}
                    </div>
                </div>
            );
        case 'phoebe':
            return <PhoebeTurn turn={turn} />;
        case 'approval':
            return (
                <div className="bg-background max-w-sm rounded-lg border p-3 text-sm shadow-sm">
                    <p className="font-medium">{turn.title}</p>
                    <p className="text-muted-foreground mt-1">{turn.body}</p>
                    {turn.preview !== undefined ? (
                        <p className="bg-muted/50 mt-2 rounded-md border p-2 text-xs">
                            {turn.preview}
                        </p>
                    ) : null}
                    <div
                        aria-hidden="true"
                        className="pointer-events-none mt-2 flex justify-end gap-1.5 text-xs select-none"
                    >
                        <span className="bg-background rounded-md border px-2.5 py-1">
                            Deny
                        </span>
                        <span className="bg-primary text-primary-foreground rounded-md px-2.5 py-1">
                            Allow ▾
                        </span>
                    </div>
                </div>
            );
        case 'chip':
            return (
                <span className="bg-background inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs">
                    <Check
                        aria-hidden="true"
                        className="size-3.5 text-green-600"
                    />
                    {turn.text}
                </span>
            );
        case 'artifact':
            return (
                <div className="bg-background flex max-w-sm items-center gap-2.5 rounded-lg border p-3 shadow-sm">
                    <FileSpreadsheet
                        aria-hidden="true"
                        className="text-muted-foreground size-5 shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">{turn.title}</p>
                        <p className="text-muted-foreground text-xs">
                            {turn.meta}
                        </p>
                    </div>
                    <span className="text-muted-foreground flex items-center text-xs">
                        Open
                        <ChevronRight aria-hidden="true" className="size-3.5" />
                    </span>
                </div>
            );
        case 'video':
            return (
                <div className="w-full max-w-sm">
                    <div
                        aria-hidden="true"
                        className="relative aspect-video w-full overflow-hidden rounded-lg border bg-neutral-900 p-2 select-none"
                    >
                        {/* Poster: a miniature of the assignment dialog the
                            walkthrough records, so the card reads as a real
                            screen recording rather than an empty player. */}
                        <div className="bg-background flex h-full w-full flex-col gap-1 rounded p-1.5 opacity-60">
                            <p className="text-[6px] font-semibold">
                                Confirm Shift Assignment
                            </p>
                            <div className="bg-muted h-2 w-full rounded-sm" />
                            <p className="text-muted-foreground text-[5px]">
                                SMS Messages to be sent:
                            </p>
                            <div className="border-border flex-1 rounded-sm border" />
                            <div className="flex justify-end gap-1">
                                <span className="bg-muted h-2 w-6 rounded-sm" />
                                <span className="bg-primary h-2 w-10 rounded-sm" />
                            </div>
                        </div>
                        <span className="absolute inset-0 flex items-center justify-center">
                            <span className="flex size-11 items-center justify-center rounded-full bg-black/45 backdrop-blur-sm">
                                <Play className="size-5 fill-white text-white" />
                            </span>
                        </span>
                        <span className="absolute bottom-2 left-2 flex size-8 items-center justify-center overflow-hidden rounded-full border-2 border-white/70 bg-neutral-700">
                            <UserRound className="size-4 text-white/80" />
                        </span>
                        <span className="absolute right-2 bottom-2 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-white">
                            {turn.durationLabel}
                        </span>
                    </div>
                    <p className="text-muted-foreground mt-1.5 text-xs">
                        Walkthrough · {turn.title}
                    </p>
                </div>
            );
        case 'divider':
            return (
                <div className="text-muted-foreground flex items-center gap-2 text-xs">
                    <span className="bg-border h-px flex-1" />
                    {turn.text}
                    <span className="bg-border h-px flex-1" />
                </div>
            );
        default:
            turn satisfies never;
            return null;
    }
}

const MENTION_GROUP_LABELS: Record<MentionPillKind, string> = {
    caregiver: 'Caregivers',
    client: 'Clients',
    shift: 'Shifts',
};

// Miniature of the real @ picker, opening upward from the composer. Shift
// rows show the client's name with the date and time on the right, matching
// how the live picker lists shifts. The @ task also passes a Shifts group
// row to teach that shifts are found by client name.
function MentionDropdownMockup({
    pill,
    shiftGroupRow,
}: {
    readonly pill: MentionPillData;
    readonly shiftGroupRow: LearnTask['pickerShiftGroupRow'] | null;
}) {
    const Icon = ENTITY_CONFIG[pill.kind].icon;
    const ShiftIcon = ENTITY_CONFIG.shift.icon;
    return (
        <div className="bg-background absolute bottom-full left-3 mb-1 w-56 rounded-md border py-1 text-xs shadow-md">
            <p className="text-muted-foreground px-2 py-0.5 font-medium">
                {MENTION_GROUP_LABELS[pill.kind]}
            </p>
            <p className="bg-muted mx-1 flex items-center gap-1.5 rounded px-1.5 py-1">
                <Icon
                    aria-hidden="true"
                    className={cn(
                        'size-3.5 shrink-0',
                        ENTITY_CONFIG[pill.kind].color,
                    )}
                />
                {pill.kind === 'shift' &&
                pill.pickerClientName !== undefined ? (
                    <Fragment>
                        {pill.pickerClientName}
                        <span className="text-muted-foreground ml-auto">
                            {pill.label}
                        </span>
                    </Fragment>
                ) : (
                    pill.label
                )}
            </p>
            {shiftGroupRow !== undefined && shiftGroupRow !== null ? (
                <Fragment>
                    <p className="text-muted-foreground px-2 pt-1 pb-0.5 font-medium">
                        Shifts
                    </p>
                    <p className="flex items-center gap-1.5 px-2.5 py-1">
                        <ShiftIcon
                            aria-hidden="true"
                            className={cn(
                                'size-3.5 shrink-0',
                                ENTITY_CONFIG.shift.color,
                            )}
                        />
                        {shiftGroupRow.clientName}
                        <span className="text-muted-foreground ml-auto">
                            {shiftGroupRow.label}
                        </span>
                    </p>
                </Fragment>
            ) : null}
        </div>
    );
}

// The composer at the bottom of the fake chat, acting out the ask in real
// time: typed text, the @ query, the picker, the committed pill, then the
// send that clears it back to the placeholder.
function ComposerBar({
    view,
    shiftGroupRow,
}: {
    readonly view: ComposerView;
    readonly shiftGroupRow: LearnTask['pickerShiftGroupRow'] | null;
}) {
    const isEmpty = view.typed.length === 0 && view.pendingQuery === '';
    return (
        <div aria-hidden="true" className="relative border-t p-3 select-none">
            {view.dropdownPill !== null ? (
                <MentionDropdownMockup
                    pill={view.dropdownPill}
                    shiftGroupRow={shiftGroupRow}
                />
            ) : null}
            <div className="min-h-9 rounded-lg border px-3 py-2 text-sm break-words">
                {view.hasSent || isEmpty ? (
                    <span className="text-muted-foreground">
                        Find shifts, callouts, or caregivers…
                    </span>
                ) : (
                    <Fragment>
                        {view.typed.map((part, index) =>
                            'pill' in part ? (
                                <MentionPill
                                    key={index}
                                    kind={part.pill.kind}
                                    label={part.pill.label}
                                />
                            ) : (
                                <Fragment key={index}>{part.text}</Fragment>
                            ),
                        )}
                        {view.pendingQuery}
                        <span className="learn-phoebe-caret">▍</span>
                    </Fragment>
                )}
            </div>
        </div>
    );
}
